import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthState } from "@atlas/auth";
import { AppHeader } from "./layout/app-header";

/**
 * Protected route component
 * - Redirects unauthenticated users to /login with the intended destination preserved
 * - Includes AppHeader for Sign Out access on all protected pages (AUTH-06)
 * - Renders child routes via Outlet
 */
export function ProtectedRoute(): React.ReactElement {
  const { isAuthenticated, isLoading } = useAuthState();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <p className="text-text-secondary text-sm">Loading...</p>
      </div>
    );
  }

  // Redirect unauthenticated users to login, preserving intended destination
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Render protected content with AppHeader for logout access
  return (
    <div className="min-h-screen flex flex-col bg-bg">
      <AppHeader />
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * Bare protected route -- authentication gate only, no header or layout.
 * Used for routes that render their own header/layout (e.g. AppLayout).
 */
export function ProtectedRouteBare(): React.ReactElement {
  const { isAuthenticated, isLoading } = useAuthState();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <p className="text-text-secondary text-sm">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <Outlet />;
}
